<template>
  <div
    v-resize="onResize"
    :style="[sizeStyles]"
    class="agr-jauge-chart"
  >
    <svg
      ref="svg"
      :viewBox="`${-svgSize / 2} ${-svgSize / 2 + padding.bottom} ${svgSize} ${svgSize / 2}`"
    >
      <defs>
        <line
          id="tick-line"
          x1="0"
          y1="0"
          :x2="thickness"
          y2="0"
          :stroke-width="borderWidth"
        />
      </defs>

      <path
        :d="`M${-radius} 0 A${radius},${radius} 0 1 1 ${radius} 0`"
        class="background"
      />

      <path
        :d="`M${-radius} 0 A${radius},${radius} 0 1 1 ${radius} 0`"
        :stroke-width="borderWidth"
        class="border"
      />

      <g
        v-for="(quarter, index) in quarters"
        :key="index"
      >
        <path
          v-if="quarter.arcBorder"
          :d="`M${quarter.arcBorder.start.x} ${quarter.arcBorder.start.y} A ${quarter.arcBorder.radius} ${quarter.arcBorder.radius} 0 0 1 ${quarter.arcBorder.end.x} ${quarter.arcBorder.end.y}`"
          :stroke-width="quarterWidth"
          :style="[quarter.arcBorder.colorData.colorStyles]"
          :class="[quarter.arcBorder.colorData.colorClasses]"
          class="quarter-arc-border"
        />

        <path
          :d="`M${quarter.arc.start.x} ${quarter.arc.start.y} A ${quarter.arc.radius} ${quarter.arc.radius} 0 0 1 ${quarter.arc.end.x} ${quarter.arc.end.y}`"
          :stroke-width="thickness"
          class="quarter-arc"
          @mouseover="$vuetify.breakpoint.mobile ? null : tooltipsVisibility[index] = true"
          @mouseleave="$vuetify.breakpoint.mobile ? null : tooltipsVisibility[index] = false"
          @click="$vuetify.breakpoint.mobile ? tooltipsVisibility[index] = true : null"
        />

        <circle
          v-if="quarter.legend"
          :id="`quarter-tooltip-activator-${index}-${id}`"
          r="0.00001"
          :cx="quarter.legend.activatorCoord.x"
          :cy="quarter.legend.activatorCoord.y"
          class="tooltip-activator"
        />
      </g>

      <g
        v-for="(tick, index) of ticks"
        :key="`tick-${index}`"
        class="tick"
      >
        <g :transform="`rotate(${tick.angle})`">
          <g :transform="`translate(-${radius} 0)`">
            <g
              v-if="index > 0 && index < ticks.length - 1"
              class="line"
            >
              <use xlink:href="#tick-line" />
            </g>

            <g
              v-if="tick.label != null"
              :transform="`rotate(${-tick.angle})`"
              :style="[tick.colorData?.colorStyles]"
              :class="[tick.colorData?.colorClasses]"
              class="legend"
            >
              <g
                :id="`legend-${index}-${id}`"
              >
                <g class="texte texte-gris-fonce">
                  <text
                    transform="translate(0, -8)"
                    text-anchor="middle"
                  >
                    {{ tick.label }}
                  </text>
                </g>

                <g
                  :style="[tick.colorData?.colorStyles]"
                  :class="[tick.colorData?.colorClasses]"
                  class="texte texte-gris-fonce"
                >
                  <text
                    transform="translate(0, 9)"
                    text-anchor="middle"
                  >
                    {{ tick.value }}{{ data.unit ? ` ${data.unit}` : '' }}

                  </text>
                </g>
              </g>
            </g>
          </g>
        </g>
      </g>

      <g
        v-if="jauge"
        :id="`jauge-${id}`"
        :style="[jauge.colorData?.colorStyles]"
        :class="[jauge.colorData?.colorClasses]"
        class="jauge texte texte-gris-fonce"
      >
        <line
          :x1="-jaugeBarLength"
          y1="0"
          :x2="0"
          y2="0"
          :stroke-width="jaugeBarWidth"
          :transform="`rotate(${jauge.angle} 0 0)`"
        />
        <circle
          cx="0"
          cy="0"
          :r="jaugeCenterRadius"
          :stroke-width="jaugeCenterWidth"
        />
        <foreignObject
          :x="-jaugeCenterRadius"
          :y="-jaugeCenterRadius"
          :width="jaugeCenterRadius * 2"
          :height="jaugeCenterRadius * 2"
        >
          <div class="icon-wrapper">
            <v-icon>
              {{ icon }}
            </v-icon>
          </div>
        </foreignObject>
      </g>
    </svg>

    <v-tooltip
      v-if="jauge"
      :activator="`#jauge-${id}`"
      content-class="bulle-comparaison"
      bottom
    >
      Mon exploitation : {{ jauge.legend }}
    </v-tooltip>

    <template v-for="(quarter, index) in quarters">
      <v-tooltip
        v-if="quarter.legend"
        :key="`tooltip-${index}`"
        v-click-outside="{ handler: () => tooltipsVisibility[index] = false, closeConditional: () => tooltipsVisibility[index] }"
        :activator="`#quarter-tooltip-activator-${index}-${id}`"
        content-class="bulle-comparaison"
        :bottom="quarter.legend.location === 'bottom'"
        :left="quarter.legend.location === 'start'"
        :right="quarter.legend.location === 'end'"
        :top="quarter.legend.location === 'top'"
        :value="tooltipsVisibility[index]"
      >
        {{ quarter.legend.text }}
      </v-tooltip>
    </template>
  </div>
</template>

<script>
import { convertToUnit } from 'vuetify/lib/util/helpers'
import { v4 as uuidv4 } from 'uuid'
import { getTextColorData } from './colorUtils'

export default {
  name: 'ComparaisonQuartiles',

  props: {
    data: {
      type: Object,
      required: true
    },
    size: {
      type: [String, Number],
      default: 460
    },
    thickness: {
      type: Number,
      default: 20
    },
    icon: {
      type: String,
      default: 'mdi-account'
    },
    iconSize: {
      type: Number,
      default: 32
    }
  },

  data () {
    return {
      id: uuidv4(),
      borderWidth: 2,
      quarterWidth: 4,
      jaugeCenterRadius: 16,
      jaugeCenterWidth: 3,
      jaugeBarWidth: 4,
      measurejaugeOffset: 5,
      legendOffset: 10,
      tooltipsVisibility: { 0: false, 1: false, 2: false, 3: false },
      svgSize: 200
    }
  },

  computed: {
    radius () {
      return (this.svgSize - this.padding.left - this.padding.right) / 2
    },
    jaugeBarLength () {
      return this.radius + 6
    },
    padding () {
      return {
        top: 50,
        bottom: this.jaugeCenterRadius + this.jaugeCenterWidth,
        left: 80,
        right: 80
      }
    },

    sizeStyles () {
      return {
        width: convertToUnit(this.size),
      }
    },

    minValue () {
      return this.ticks[0].value
    },

    maxValue () {
      return this.ticks[this.ticks.length - 1].value
    },

    ticks () {
      return [
        {
          angle: 0,
          value: this.data.min.value,
          label: this.data.min.label,
          colorData: getTextColorData(this.data.quarters[0]?.color)
        },
        {
          angle: 45,
          value: this.data.quartile1.value,
          label: this.data.quartile1.label,
          colorData: getTextColorData(this.data.quarters[0]?.color)
        },
        {
          angle: 90,
          value: this.data.mediane.value,
          label: this.data.mediane.label,
        },
        {
          angle: 135,
          value: this.data.quartile2.value,
          label: this.data.quartile2.label,
          colorData: getTextColorData(this.data.quarters[3]?.color)
        },
        {
          angle: 180,
          value: this.data.max.value,
          label: this.data.max.label,
          colorData: getTextColorData(this.data.quarters[3]?.color)
        }
      ]
    },

    quarters () {
      const quarters = []
      for (let i = 0 ; i < 4; i++) {
        const minTick = this.ticks[i]
        const maxTick = this.ticks[i + 1]

        const quarterData = this.data.quarters[i]

        const angleMin = minTick.angle
        const angleMax = maxTick.angle

        const quarterBorderRadius = this.radius
        const quarterRadius = this.radius - (this.thickness / 2)
        const quarter = {
          min: minTick.value,
          max: maxTick.value,
          angleMin,
          angleMax,
          arc: {
            radius: quarterRadius,
            start: {
              x: this.getCoordX(angleMin, quarterRadius),
              y: this.getCoordY(angleMin, quarterRadius)
            },
            end: {
              x: this.getCoordX(angleMax, quarterRadius),
              y: this.getCoordY(angleMax, quarterRadius)
            }
          }
        }

        if (quarterData.color != null) {
          quarter.arcBorder = {
            radius: quarterBorderRadius,
            colorData: getTextColorData(quarterData.color),
            start: {
              x: this.getCoordX(angleMin, quarterBorderRadius),
              y: this.getCoordY(angleMin, quarterBorderRadius)
            },
            end: {
              x: this.getCoordX(angleMax, quarterBorderRadius),
              y: this.getCoordY(angleMax, quarterBorderRadius)
            }
          }
        }

        if (quarterData.legend != null) {
          const angle = angleMin + (angleMax - angleMin) / 2

          const x = this.getCoordX(angle, 1)
          let location = (x > 0) ? 'end' : 'start'

          quarter.legend = {
            text: quarterData.legend,
            location,
            activatorCoord: {
              x: this.getCoordX(angle, this.radius),
              y: this.getCoordY(angle, this.radius)
            }
          }
        }

        quarters.push(quarter)
      }

      return quarters
    },

    jauge () {
      if (this.data.measure != null) {
        const valuePerso = this.data.measure.value
        const quarter = this.quarters.find(
          (q) =>
            (valuePerso > q.min &&
            valuePerso <= q.max) || valuePerso >= q.min &&
            valuePerso < q.max
        )

        if (quarter != null) {
          const percentage = (valuePerso - quarter.min) / (quarter.max - quarter.min)

          return {
            colorData: quarter.arcBorder?.colorData,
            angle: quarter.angleMin + (quarter.angleMax - quarter.angleMin) * percentage,
            legend: this.data.measure.legend
          }
        }
      }

      return null
    }
  },

  mounted () {
    this.onResize()
    for (const [index, tick] of this.ticks.entries()) {
      if (tick.label != null) {
        const legendElement = document.getElementById(
          `legend-${index}-${this.id}`
        )

        const legendWidth = legendElement.getBBox().width
        const translateX =
          this.getCoordX(tick.angle, 1) * (legendWidth / 2 + this.legendOffset)

        const legendHeight = legendElement.getBBox().height
        const translateY =
          this.getCoordY(tick.angle, 1) * (legendHeight / 2 + this.legendOffset)

        legendElement.setAttribute(
          'transform',
          `translate(${translateX} ${translateY})`
        )
      }
    }
  },

  methods: {
    onResize () {
      this.svgSize = this.$el.offsetWidth > 0 ? this.$el.offsetWidth : 600 
    },
    getTrigoAngle (angle) {
      return ((180 - angle) * Math.PI) / 180
    },

    getCoordX (angle, radius) {
      return Math.cos(this.getTrigoAngle(angle)) * radius
    },

    getCoordY (angle, radius) {
      return -Math.sin(this.getTrigoAngle(angle)) * radius
    }
  }
}
</script>

<style lang="scss" scoped>
.agr-jauge-chart {
  height: auto;

  svg {
    width: 100%;
    height: auto;

    .background {
      fill: #f1f2f5;
      stroke: none;
      filter: drop-shadow(0px 3px 1px var(--v-shadow-key-umbra-opacity, rgba(0, 0, 0, 0.2)));
    }

    .border {
      fill: none;
      stroke: #d7d8d6;
    }

    .tick {
      .line {
        stroke: #d7d8d6;
        stroke-linecap: round;
      }

      .legend {
        text {
          fill: currentColor;
        }
      }
    }

    .jauge {
      fill: none;
      stroke: currentColor;
      stroke-linecap: round;

      &:focus {
        outline: none;
      }

      circle {
        fill: #ffffff;
      }

      .icon-wrapper {
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;

        .v-icon {
          color: currentColor;
        }
      }
    }

     .quarter-arc-border {
      fill: transparent;
      stroke: currentColor;
    }

    .quarter-arc {
      fill: transparent;
      stroke: #ffffff;
    }

    .tooltip-activator {
      fill: transparent;
      stroke: transparent;
    }

    &.disabled {
      opacity: 0.25;
    }
  }
}
</style>
